import type { LatLng } from 'leaflet'

export const MIN_LAT = -85.05112878
export const MAX_LAT = 85.05112878
export const MIN_LNG = -180
export const MAX_LNG = 180
export const MAX_ZOOM_LEVEL = 23
export const MIN_ZOOM_LEVEL = 0
export const EQUATOR = 40075016.68557849
export const TILE_SIZE = 256

export class Quadkey {
  quadkey: string
  zoomLevel: number
  northWest: LatLng

  static createFromLatLng(latlng: LatLng, zoomLevel: number): Quadkey {
    const quadkey = Quadkey.latlngToQuadkey(latlng, zoomLevel)
    return new Quadkey(quadkey)
  }

  static createFromTile(tileX: number, tileY: number, zoomLevel: number): Quadkey {
    const quadkey = Quadkey.tileToQuadkey(tileX, tileY, zoomLevel)
    return new Quadkey(quadkey)
  }

  static createFromCircle(latlng: LatLng, radius: number): Array<Quadkey | null> {
    const zoom = Quadkey.calcZoomLevelContainCircle(latlng.lat, radius)
    const center = Quadkey.createFromLatLng(latlng, zoom)
    const top = center.north()
    const topLeft = top !== null ? top.west() : null
    const topRight = top !== null ? top.east() : null
    const left = center.west()
    const right = center.east()
    const bottom = center.south()
    const bottomLeft = bottom !== null ? bottom.west() : null
    const bottomRight = bottom !== null ? bottom.east() : null
    return [topLeft, top, topRight, left, center, right, bottomLeft, bottom, bottomRight]
  }

  constructor(quadkey: string) {
    this.quadkey = quadkey
    this.zoomLevel = quadkey.length
    this.northWest = Quadkey.northWestLatLng(this.quadkey, this.zoomLevel)
  }

  north(): Quadkey | null {
    const tiles = Quadkey.quadkeyToTile(this.quadkey)
    if (tiles[1] === 0) {
      return null
    }
    const tileY = tiles[1] - 1
    const newKey = Quadkey.tileToQuadkey(tiles[0], tileY, this.zoomLevel)
    return new Quadkey(newKey)
  }

  south(): Quadkey | null {
    const tiles = Quadkey.quadkeyToTile(this.quadkey)
    const meshCount = Math.floor(Quadkey.calcMapSize(this.zoomLevel) / TILE_SIZE)
    if (meshCount - 1 <= tiles[1]) {
      return null
    }
    const tileY = tiles[1] + 1
    const newKey = Quadkey.tileToQuadkey(tiles[0], tileY, this.zoomLevel)
    return new Quadkey(newKey)
  }

  west(): Quadkey | null {
    const tiles = Quadkey.quadkeyToTile(this.quadkey)
    if (tiles[0] === 0) {
      return null
    }
    const tileX = tiles[0] - 1
    const newKey = Quadkey.tileToQuadkey(tileX, tiles[1], this.zoomLevel)
    return new Quadkey(newKey)
  }

  east(): Quadkey | null {
    const tiles = Quadkey.quadkeyToTile(this.quadkey)
    const meshCount = Math.floor(Quadkey.calcMapSize(this.zoomLevel) / TILE_SIZE)
    if (meshCount - 1 <= tiles[0]) {
      return null
    }
    const tileX = tiles[0] + 1
    const newKey = Quadkey.tileToQuadkey(tileX, tiles[1], this.zoomLevel)
    return new Quadkey(newKey)
  }

  corners(): LatLng[] | null {
    const east = this.east()
    const south = this.south()
    if (east === null || south === null) {
      return null
    }
    const northWest = { lng: this.northWest.lng, lat: this.northWest.lat } as LatLng
    const southWest = { lng: this.northWest.lng, lat: south.northWest.lat } as LatLng
    const southEast = { lng: east.northWest.lng, lat: south.northWest.lat } as LatLng
    const northEast = { lng: east.northWest.lng, lat: this.northWest.lat } as LatLng
    return [northWest, southWest, southEast, northEast]
  }

  children(zoomLevel: number = MAX_ZOOM_LEVEL): Quadkey[] {
    if (this.zoomLevel >= zoomLevel) {
      return []
    }
    const corners = this.corners()
    if (corners === null) {
      return []
    }
    const northWestKey = Quadkey.createFromLatLng(corners[0], zoomLevel)
    const southEastKey = Quadkey.createFromLatLng(corners[2], zoomLevel)
    const keys: Quadkey[] = [northWestKey]
    const northWestTile = northWestKey.tile()
    const southEastTile = southEastKey.tile()
    for (let tileY = northWestTile[1] + 1; tileY < southEastTile[1]; ++tileY) {
      for (let tileX = northWestTile[0] + 1; tileX < southEastTile[0]; ++tileX) {
        const key = Quadkey.createFromTile(tileX, tileY, zoomLevel)
        keys.push(key)
      }
    }
    return keys
  }

  tile(): number[] {
    return Quadkey.quadkeyToTile(this.quadkey)
  }

  static northWestLatLng(quadkey: string, zoomLevel: number): LatLng {
    const tile = Quadkey.quadkeyToTile(quadkey)
    const pixel = Quadkey.tileToPixel(tile[0], tile[1])
    const latlng = Quadkey.pixelToLatLng(pixel[0], pixel[1], zoomLevel)
    return {
      lat: latlng[0],
      lng: latlng[1]
    } as LatLng
  }

  static clip(val: number, minVal: number, maxVal: number): number {
    return Math.min(Math.max(val, minVal), maxVal)
  }

  static calcMapSize(zoomLevel: number): number {
    return TILE_SIZE * Math.pow(2, zoomLevel)
  }

  static latlngToQuadkey(latlng: LatLng, zoomLevel: number): string {
    const pixels = Quadkey.latlngToPixel(latlng, zoomLevel)
    const tiles = Quadkey.pixelToTile(pixels[0], pixels[1])
    return Quadkey.tileToQuadkey(tiles[0], tiles[1], zoomLevel)
  }

  static latlngToPixel(latlng: LatLng, zoomLevel: number): number[] {
    const latClip = Quadkey.clip(latlng.lat, MIN_LAT, MAX_LAT)
    const lngClip = Quadkey.clip(latlng.lng, MIN_LNG, MAX_LNG)

    const x = (lngClip + 180) / 360
    const latSin = Math.sin((latClip * Math.PI) / 180)
    const y = 0.5 - Math.log((1 + latSin) / (1 - latSin)) / (4 * Math.PI)

    const mapSize = this.calcMapSize(zoomLevel)

    const pixelX = Math.floor(this.clip(x * mapSize + 0.5, 0, mapSize - 1))
    const pixelY = Math.floor(this.clip(y * mapSize + 0.5, 0, mapSize - 1))
    return [pixelX, pixelY]
  }

  static pixelToLatLng(pixelX: number, pixelY: number, zoomLevel: number): number[] {
    const mapSize = Quadkey.calcMapSize(zoomLevel)
    const x = Quadkey.clip(pixelX, 0, mapSize - 1) / mapSize - 0.5
    const y = 0.5 - Quadkey.clip(pixelY, 0, mapSize - 1) / mapSize

    const lat = 90 - (360 * Math.atan(Math.exp(-y * 2 * Math.PI))) / Math.PI
    const lng = 360 * x
    return [lat, lng]
  }

  static pixelToTile(pixelX: number, pixelY: number): number[] {
    const tileX = Math.floor(pixelX / TILE_SIZE)
    const tileY = Math.floor(pixelY / TILE_SIZE)
    return [tileX, tileY]
  }

  static tileToPixel(tileX: number, tileY: number): number[] {
    const pixelX = tileX * 256
    const pixelY = tileY * 256
    return [pixelX, pixelY]
  }

  static tileToQuadkey(tileX: number, tileY: number, zoomLevel: number): string {
    let quadkey = ''
    for (let i = zoomLevel; i > MIN_ZOOM_LEVEL; --i) {
      let digit = 0
      const mask = 1 << (i - 1)
      if ((tileX & mask) > 0) {
        digit += 1
      }
      if ((tileY & mask) > 0) {
        digit += 2
      }
      quadkey += digit.toString()
    }

    return quadkey
  }

  static quadkeyToLatLng(quadkey: string): LatLng {
    const key = new Quadkey(quadkey)
    const tile = Quadkey.quadkeyToTile(quadkey)
    const pixel = Quadkey.tileToPixel(tile[0], tile[1])
    const latlng = Quadkey.pixelToLatLng(pixel[0], pixel[1], key.zoomLevel)
    return { lat: latlng[0], lng: latlng[1] } as LatLng
  }

  static quadkeyToTile(quadkey: string): number[] {
    const zoomLevel = quadkey.length
    let tileX = 0
    let tileY = 0
    const digits = quadkey.split('')
    for (let i = zoomLevel; i > MIN_ZOOM_LEVEL; --i) {
      const mask = 1 << (i - 1)
      const digit = digits[zoomLevel - i]
      switch (digit) {
        case '1':
          tileX |= mask
          break
        case '2':
          tileY |= mask
          break
        case '3':
          tileX |= mask
          tileY |= mask
          break
        default:
          break
      }
    }

    return [tileX, tileY]
  }

  static calcZoomLevelContainCircle(lat: number, radius: number): number {
    const normRad = radius / Math.cos((lat * Math.PI) / 180)
    for (let i = MAX_ZOOM_LEVEL; i > 0; --i) {
      const meshLen = EQUATOR / Math.pow(2, i)
      if (normRad <= meshLen) {
        return i
      }
    }
    return 0
  }

  static calcZoomLevelByLength(length: number): number[] {
    for (let i = MAX_ZOOM_LEVEL; i > 0; --i) {
      const meshLen = EQUATOR / Math.pow(2, i)
      if (length <= meshLen) {
        return [i, meshLen]
      }
    }
    return [0, EQUATOR]
  }
}
